import axios from 'axios';
import classNames from 'classnames';
import Button from 'components/base/Button';
import DietitianTable, {
  DietitianTableColumns
} from 'components/tables/superadmin/DietitianTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import * as formik from 'formik';
import { errorHandler } from 'helpers/response-utils';
import { useEffect, useState } from 'react';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import Card from 'react-bootstrap/Card';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { clinicValidationSchmaForUpdateAdmin } from 'static/Clinic';
import * as yup from 'yup';
import QRCode from 'qrcode';

const SAEditClinic = () => {
  const navigate = useNavigate();
  const { Formik } = formik;
  const { id } = useParams();
  const token = localStorage.getItem('token');
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [dietitians, setDietitians] = useState<any[]>([]);
  const [editingDietitian, setEditingDietitian] = useState<any>(null);
  const [isDietitianModalOpen, setIsDietitianModalOpen] = useState(false);
  const [isRemoveConfirmationOpen, setIsRemoveConfirmationOpen] =
    useState(false);
  const [dietitianForm, setDietitianForm] = useState({
    firstname: '',
    email: '',
    password: ''
  });
  const [clinic, setClinic] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    postcode: '',
    code: '',
    is_active: false,
    isDisplayFoodData: true,
    clinical_export_enabled: false,
    research_export_enabled: false
  });

  const schema = yup.object().shape(clinicValidationSchmaForUpdateAdmin);

  useEffect(() => {
    getClinic();
  }, []);

  const getClinic = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/clinic/sa/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === false) {
        errorHandler(response.data);
        return;
      }

      const data = response.data.data.clinic;
      setQrCodeImage(await QRCode.toDataURL(data.code));
      setClinic({
        name: data.name,
        email: data.email,
        phone: data.phone,
        postcode: data.postcode,
        address: data.address,
        code: data.code,
        is_active: data.is_active,
        isDisplayFoodData: data.isDisplayFoodData,
        clinical_export_enabled: data.user?.clinical_export_enabled ?? false,
        research_export_enabled: data.user?.research_export_enabled ?? false
      });
      getDietitians();
    } catch (error) {
      console.error('Error occurred:', error);
    }
  };

  const getDietitians = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/clinic/${id}/dietitians`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === false) {
        errorHandler(response.data);
        return;
      }

      setDietitians(response.data.data.dietitians || []);
    } catch (error) {
      console.error('Error occurred while loading dietitians:', error);
    }
  };

  const addDietitian = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/clinic/${id}/dietitians`,
        dietitianForm,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === false) {
        toast.error(response.data.error.message);
        return;
      }

      toast(response.data.data.message);
      setDietitianForm({ firstname: '', email: '', password: '' });
      setIsDietitianModalOpen(false);
      getDietitians();
    } catch (error) {
      console.error('Error occurred while adding dietitian:', error);
    }
  };

  const updateDietitian = async () => {
    if (!editingDietitian) return;

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_ENDPOINT}/clinic/${id}/dietitians/${editingDietitian.id}`,
        dietitianForm,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === false) {
        toast.error(response.data.error.message);
        return;
      }

      toast(response.data.data.message);
      setEditingDietitian(null);
      setDietitianForm({ firstname: '', email: '', password: '' });
      setIsDietitianModalOpen(false);
      getDietitians();
    } catch (error) {
      console.error('Error occurred while updating dietitian:', error);
    }
  };

  const removeDietitian = async () => {
    if (!editingDietitian) return;

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_ENDPOINT}/clinic/${id}/dietitians/${editingDietitian.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === false) {
        errorHandler(response.data);
        return;
      }

      toast.success('Dietitian deleted successfully!');
      setIsRemoveConfirmationOpen(false);
      cancelEditingDietitian();
      getDietitians();
    } catch (error) {
      console.error('Error deleting dietitian:', error);
      toast.error('Failed to delete dietitian');
    }
  };

  const startEditingDietitian = (dietitian: any) => {
    setEditingDietitian(dietitian);
    setDietitianForm({
      firstname: dietitian.firstname || '',
      email: dietitian.email || '',
      password: ''
    });
    setIsDietitianModalOpen(true);
  };

  const cancelEditingDietitian = () => {
    setEditingDietitian(null);
    setDietitianForm({ firstname: '', email: '', password: '' });
    setIsRemoveConfirmationOpen(false);
    setIsDietitianModalOpen(false);
  };

  const openRemoveConfirmation = () => {
    setIsRemoveConfirmationOpen(true);
  };

  const closeRemoveConfirmation = () => {
    setIsRemoveConfirmationOpen(false);
  };

  const openAddDietitianModal = () => {
    setEditingDietitian(null);
    setDietitianForm({ firstname: '', email: '', password: '' });
    setIsDietitianModalOpen(true);
  };

  const generateDietitianPassword = (length = 12) => {
    const charset =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=';
    let password = '';
    for (let index = 0; index < length; index++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }
    setDietitianForm(currentForm => ({ ...currentForm, password }));
  };

  const onSubmit = async (values: any) => {
    const data = {
      name: values.name,
      email: values.email,
      address: values.address,
      phone: values.phone,
      postcode: values.postcode,
      is_active: values.is_active,
      isDisplayFoodData: values.isDisplayFoodData,
      clinical_export_enabled: values.clinical_export_enabled,
      research_export_enabled: values.research_export_enabled
    };
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_ENDPOINT}/clinic/sa/${id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === false) {
        toast.error(response.data.error.message);
        return;
      }

      toast(response.data.data.message);
      navigateBack();
    } catch (error) {
      console.error('Error occurred:', error);
    }
  };

  const navigateBack = () => {
    navigate('/superadmin/clinic/list');
  };

  const table = useAdvanceTable({
    data: dietitians,
    columns: DietitianTableColumns(id, getDietitians, startEditingDietitian),
    pageSize: 10,
    pagination: false,
    sortable: false,
    selection: false
  });

  return (
    <Card className="col-12">
      <Card.Header>
        <div className="d-sm-flex justify-content-between">
          <h2>Edit clinic</h2>
        </div>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col xl={12}>
            <Formik
              validationSchema={schema}
              onSubmit={values => onSubmit(values)}
              initialValues={clinic}
            >
              {({
                handleSubmit,
                handleChange,
                values,
                touched,
                errors,
                setValues
              }) => {
                useEffect(() => {
                  setValues(clinic);
                }, [clinic]);
                return (
                  <Form noValidate onSubmit={handleSubmit}>
                    <Row className={classNames('g-3', 'mb-6')}>
                      <Col sm={6} md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>
                            Name <span className="text-danger ms-1">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={values.name}
                            onChange={handleChange}
                            isValid={touched.name && !errors.name}
                            isInvalid={touched.name && !!errors.name}
                          />
                          <Form.Control.Feedback type="invalid">
                            {' '}
                            {errors.name}{' '}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col sm={6} md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>
                            Email Address{' '}
                            <span className="text-danger ms-1">*</span>
                          </Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            isValid={touched.email && !errors.email}
                            isInvalid={touched.email && !!errors.email}
                          />
                          <Form.Control.Feedback type="invalid">
                            {' '}
                            {errors.email}{' '}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col sm={6} md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>Contact</Form.Label>
                          <Form.Control
                            type="text"
                            name="phone"
                            value={values.phone}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6} md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>Postcode</Form.Label>
                          <Form.Control
                            type="text"
                            name="postcode"
                            value={values.postcode}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col sm={6} md={6}>
                        <Form.Group className="mb-2">
                          <Form.Check
                            type="switch"
                            name="clinical_export_enabled"
                            id="clinical_export_enabled"
                            label="Allow clinical data export? (With real patient names)"
                            checked={values.clinical_export_enabled}
                            onChange={e =>
                              setValues({
                                ...values,
                                clinical_export_enabled: e.target.checked
                              })
                            }
                          />
                        </Form.Group>
                      </Col>

                      <Col sm={6} md={6}>
                        <Form.Group className="mb-2">
                          <Form.Check
                            type="switch"
                            name="research_export_enabled"
                            id="research_export_enabled"
                            label="Allow research data export? (Without real patient names)"
                            checked={values.research_export_enabled}
                            onChange={e =>
                              setValues({
                                ...values,
                                research_export_enabled: e.target.checked
                              })
                            }
                          />
                        </Form.Group>
                      </Col>

                      <Col sm={8} md={8}>
                        <Row className={classNames('g-3', 'mb-6')}>
                          <Col sm={6} md={6}>
                            <Form.Group className="mb-2">
                              <Form.Label>Code</Form.Label>
                              <Form.Control
                                type="text"
                                name="code"
                                placeholder="Code"
                                value={values.code}
                                disabled
                              />
                            </Form.Group>
                          </Col>
                          <Col sm={3} md={3}>
                            {' '}
                            <br />
                            <Button
                              variant="outline-primary"
                              className="me-2 px-6"
                              disabled
                            >
                              Generate
                            </Button>{' '}
                          </Col>
                        </Row>
                      </Col>

                      <Col sm={3} md={3}>
                        <a href={qrCodeImage} download={values.code}>
                          <img src={qrCodeImage} alt="" className="w-50" />
                        </a>
                      </Col>

                      <Col sm={12} md={12}>
                        <Form.Group className="mb-2">
                          <Form.Label>Address</Form.Label>
                          <Form.Control
                            as="textarea"
                            name="address"
                            value={values.address}
                            onChange={handleChange}
                            rows={4}
                          />
                        </Form.Group>
                      </Col>

                      <Col sm={12} md={12}>
                        <Form.Group className="mb-2">
                          <Form.Check
                            type="switch"
                            name="is_active"
                            id="is_active"
                            label={`Would you like to ${
                              values.is_active ? 'Deactivate' : 'Active'
                            } Clinic?`}
                            checked={values.is_active}
                            onChange={e =>
                              setValues({
                                ...values,
                                is_active: e.target.checked
                              })
                            }
                          />
                        </Form.Group>
                      </Col>

                      <Col sm={12} md={12}>
                        <Form.Group className="mb-2">
                          <Form.Check
                            type="switch"
                            name="isDisplayFoodData"
                            id="isDisplayFoodData"
                            label="Allow clinic to see the patient food data?"
                            checked={values.isDisplayFoodData}
                            onChange={e =>
                              setValues({
                                ...values,
                                isDisplayFoodData: e.target.checked
                              })
                            }
                          />
                        </Form.Group>
                      </Col>

                      <Col sm={12} md={12}>
                        <Card>
                          <Card.Header>
                            <div className="d-flex align-items-center justify-content-between">
                              <h4 className="mb-0">Dietitians</h4>
                              <Button
                                type="button"
                                variant="primary"
                                onClick={openAddDietitianModal}
                              >
                                Add Dietitian
                              </Button>
                            </div>
                          </Card.Header>
                          <Card.Body>
                            {dietitians.length ? (
                              <div>
                                <AdvanceTableProvider {...table}>
                                  <DietitianTable
                                    clinicId={id}
                                    onDeleteSuccess={getDietitians}
                                    onEditDietitian={startEditingDietitian}
                                  />
                                </AdvanceTableProvider>
                              </div>
                            ) : (
                              <p className="text-body-secondary">
                                No dietitians have been added yet.
                              </p>
                            )}
                            <Modal
                              show={isDietitianModalOpen}
                              onHide={cancelEditingDietitian}
                              centered
                            >
                              <Modal.Header closeButton>
                                <Modal.Title>
                                  {editingDietitian
                                    ? 'Edit Dietitian'
                                    : 'Add Dietitian'}
                                </Modal.Title>
                              </Modal.Header>
                              <Modal.Body>
                                <Row className="g-3">
                                  <Col sm={12}>
                                    <Form.Group>
                                      <Form.Label>
                                        First name{' '}
                                        <span className="text-danger ms-1">
                                          *
                                        </span>
                                      </Form.Label>
                                      <Form.Control
                                        type="text"
                                        value={dietitianForm.firstname}
                                        onChange={event =>
                                          setDietitianForm({
                                            ...dietitianForm,
                                            firstname: event.target.value
                                          })
                                        }
                                        required
                                      />
                                    </Form.Group>
                                  </Col>
                                  <Col sm={12}>
                                    <Form.Group>
                                      <Form.Label>
                                        Email Address{' '}
                                        <span className="text-danger ms-1">
                                          *
                                        </span>
                                      </Form.Label>
                                      <Form.Control
                                        type="email"
                                        value={dietitianForm.email}
                                        onChange={event =>
                                          setDietitianForm({
                                            ...dietitianForm,
                                            email: event.target.value
                                          })
                                        }
                                        required
                                      />
                                    </Form.Group>
                                  </Col>
                                  {!editingDietitian && (
                                    <Col sm={12}>
                                      <Form.Group>
                                        <Form.Label>
                                          Password{' '}
                                          <span className="text-danger ms-1">
                                            *
                                          </span>
                                        </Form.Label>
                                        <div className="d-flex gap-2">
                                          <Form.Control
                                            type="text"
                                            value={dietitianForm.password}
                                            onChange={event =>
                                              setDietitianForm({
                                                ...dietitianForm,
                                                password: event.target.value
                                              })
                                            }
                                            required
                                          />
                                          <Button
                                            type="button"
                                            variant="outline-primary"
                                            onClick={() =>
                                              generateDietitianPassword(12)
                                            }
                                          >
                                            Generate
                                          </Button>
                                        </div>
                                      </Form.Group>
                                    </Col>
                                  )}
                                </Row>
                              </Modal.Body>
                              <Modal.Footer>
                                {editingDietitian && (
                                  <Button
                                    type="button"
                                    variant="danger"
                                    className="me-auto"
                                    onClick={openRemoveConfirmation}
                                  >
                                    Remove
                                  </Button>
                                )}
                                <Button
                                  type="button"
                                  variant="secondary"
                                  onClick={cancelEditingDietitian}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  type="button"
                                  variant="primary"
                                  onClick={
                                    editingDietitian
                                      ? updateDietitian
                                      : addDietitian
                                  }
                                >
                                  {editingDietitian
                                    ? 'Update Dietitian'
                                    : 'Add Dietitian'}
                                </Button>
                              </Modal.Footer>
                            </Modal>
                            <Modal
                              show={isRemoveConfirmationOpen}
                              onHide={closeRemoveConfirmation}
                              backdrop="static"
                              keyboard={false}
                              centered
                            >
                              <Modal.Header>
                                <Modal.Title>Are you sure?</Modal.Title>
                              </Modal.Header>
                              <Modal.Body>
                                Are you sure you want to delete this user?
                              </Modal.Body>
                              <Modal.Footer>
                                <Button
                                  type="button"
                                  variant="secondary"
                                  onClick={closeRemoveConfirmation}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  type="button"
                                  variant="danger"
                                  onClick={removeDietitian}
                                >
                                  Delete
                                </Button>
                              </Modal.Footer>
                            </Modal>
                          </Card.Body>
                        </Card>
                      </Col>

                      <div className="text-end">
                        <Button
                          variant="phoenix-primary"
                          className="me-2 px-6"
                          onClick={navigateBack}
                        >
                          Cancel
                        </Button>
                        <Button type="submit" variant="primary">
                          Update
                        </Button>
                      </div>
                    </Row>
                  </Form>
                );
              }}
            </Formik>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default SAEditClinic;
